<template>
  <!-- eslint-disable vue/no-unused-refs -->
  <AppBarLayout>
    <template #default="{ activeHierarchy }">
      <div class="main">
        <BrandPattern4 ref="animation" page="APPBARLAYOUT" />
        <backgroundComponent ref="backgroundRef" />
        <ActivePathWrapper
          class="tw-px-[20px] md:tw-px-[32px] xl:tw-px-[79px]"
          :active-hierarchy="activeHierarchy"
        />
        <div
          class="header-container tw-bg-white xl:tw-px-[60px] xl:tw-py-[55px] tw-px-[10px] tw-py-[20px]"
          style="align-items: center; display: flex"
        >
          <span class="header-text">{{
            getUiText("app-settings-domain-title-text-1")
          }}</span>
        </div>
        <div
          class="domain-content main-content tw-relative xl:tw-px-[60px] xl:tw-py-[55px] tw-px-[10px] tw-py-[10px]"
        >
          <div
            class="tw-flex align-items-start xl:tw-flex-row tw-flex-col-reverse"
          >
            <div id="clickbox-domain" class="tw-left-sidebar org-modal-contain">
              <div
                class="first-row tw-flex tw-w-full tw-justify-between tw-items-center"
              >
                <div class="search-box tw-flex tw-justify-center">
                  <img src="/img/search_black_icon.svg" alt="search_icon" />
                  <input
                    v-model="search"
                    type="text"
                    :placeholder="getUiText('app-table-search-prompt-text-1')"
                    class="tw-placeholder-[#B7B7B7]"
                  />
                </div>
                <div class="tw-flex tw-items-center bulk__action">
                  <div @click="handleShowBulkAction">
                    <ButtonBulkActions
                      :active="showBulkActions"
                      :id-popover="'popover-button-bulk-actions-domain-management'"
                      :class="`!tw-border tw-border-1 !tw-border-solid !tw-border-[#EFF0F1]`"
                      :show-more-actions="isShowMoreActions"
                      :show-acction-click="true"
                      :selected-number="multipleSelectedDomains.length"
                    >
                      <CustomDropDownItem
                        :style="'danger'"
                        :id-text="'app-common-action-remove-title-1'"
                        @click="deleteDomains"
                      />
                    </ButtonBulkActions>
                  </div>
                  <ButtonFilter
                    :active="false"
                    :id-popover="'popover-domain-management'"
                    :is-margin-right="false"
                  />
                  <IconButton
                    data-cy="add-domain-button"
                    src="/img/plus_icon.svg"
                    @click="addDomain"
                  />
                </div>
              </div>

              <div class="tw-rounded-md table-size">
                <DomainTable
                  :table-data="orgDomainsFiltered"
                  :table-fields="tableFields"
                  :loading="loadingDomainsFields"
                  @verify-domain="handleVerifyDomain"
                  @manage-domain="handleShowModalManageDomain"
                  @delete="deleteDomainByID"
                  @select:row="handleSelectDomain"
                  @un-verify-domain="handleUnVerifyDomain"
                  @un-verify-email="handleUnVerifyEmail"
                  @send-verification-email="showModalSendVerificationEmail"
                />
              </div>
            </div>
            <div
              v-if="loadingDomainsFields"
              class="tw-right-sidebar skeleton-domain-sidebar"
            >
              <div class="skeleton-radial custom-global-skeleton-pulse"></div>
              <div class="skeleton-text-container">
                <div
                  class="skeleton-percent custom-global-skeleton-pulse"
                ></div>
                <div class="skeleton-text custom-global-skeleton-pulse"></div>
              </div>
            </div>
            <div
              v-else-if="!orgDomainsVerify?.length"
              :class="'tw-right-sidebar bg-transparent'"
            />
            <div
              v-else
              :class="[
                'tw-right-sidebar',
                orgDomainsVerify.length ? 'tw-bg-[#FFFFFF]' : '',
              ]"
            >
              <RadialProgressBar
                class="d-inline"
                :total-steps="orgDomainsVerify.length"
                :completed-steps="getCompletedSteps()"
                :is-hide-progress="true"
                outer-stroke-color="#48A0FF"
              />
              <div>
                <div class="number-per">
                  {{ getProgressCompelete() }}
                </div>
                <div class="text-per">
                  {{ getUiText("app-settings-domain-progress-text-1") }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </AppBarLayout>
  <!-- Modal : Add Domain -->
  <AddDomainModal
    :show="isShowModalAddDomain"
    @update:show="isShowModalAddDomain = $event"
    @add:domain:success="addDomainSuccess"
  />

  <!-- Modal: manage Domain -->
  <ManageDomainModal
    :show="isShowModalManageDomain"
    :domain="singleSelectedDomains"
    @update:show="onManageDomainModalClose"
    @update:domain="updateDomainVerified"
    @delete:domain="deleteDomainByID($event)"
  />

  <!-- Modal: Verify Email -->
  <VerifyEmailModal
    :show="isShowModalVerifyEmail"
    :domain="singleSelectedDomains"
    @update:show="closeModalSendVerificationEmail"
    @send-mail="closeModalSendVerificationEmail"
  />

  <!-- Delete Domain modal -->
  <DeleteDomainModal
    :show="isDeleteModalVisible"
    :header="true"
    :red-btn="true"
    header-title="app-settings-domain-delete-domain-1"
    :is-loading-delete="isLoadingDelete"
    :contain-title="
      isHandleMultipleSelect
        ? multipleSelectedDomains.map((d) => d.domain)
        : [singleSelectedDomains.domain]
    "
    contain-sub-title="app-settings-domain-delete-modal-helper-text-a-1"
    action-title="app-settings-domain-delete-domain-1"
    @update:show="
      ($event) => {
        isHandleMultipleSelect = false;
        isDeleteModalVisible = $event;
      }
    "
    @update:action-handle="closeDeleteModal"
  />
</template>

<script lang="ts" setup>
import store from "@/store";
import { Ref, computed, onMounted, onUnmounted, ref, watch } from "vue";
import utilities, { getComponentUiText } from "@/utils/utilities";
import AppBarLayout from "@/components/layouts/appBarLayout.vue";
import RadialProgressBar from "@/components/prospect/prospectQuestions/componentTypes/radialProgressBar.vue";
import ButtonFilter from "@/components/common/setting/buttonFilter.vue";
import DomainTable from "@/pages/domains/domainTable.vue";
import IconButton from "@/components/common/iconButton.vue";
import ButtonBulkActions from "@/components/common/setting/buttonBulkActions.vue";
import AddDomainModal from "@/components/domains/addDomainModal.vue";
import VerifyAddDomainModal from "@/components/domains/verifyAddDomainModal.vue";
import VerifyEmailModal from "@/components/domains/verifyEmailModal.vue";
import ManageDomainModal from "@/components/domains/manageDomainModal.vue";
import BrandPattern4 from "@/components/prospect/brandPattern/brandPattern4.vue";
import backgroundComponent from "@/components/common/backgroundComponent.vue";
import ActivePathWrapper from "@/components/common/activePathWrapper.vue";
import {
  deleteOrgDomains,
  getOrgDomains,
  postDeleteOrgDomain,
  postOrgDomainVerify,
  unVerifyDomain,
  unVerifyDomainEmail,
} from "@/services/org";
import { IDomain } from "@/interfaces/org/domain";
import get from "lodash/get";
import {
  ARE_YOU_SURE_TYPES,
  MODAL_NAME,
  localStorageKey,
  manageDomainActions,
} from "@/utils/constants";
import DeleteDomainModal from "@/components/domains/deleteDomainModal.vue";
import commonServices from "@/services/common/commonServices";
import EventEmitter, { EmitterEvents } from "@/utils/eventEmitter";
import { IToastMessage, ToastVariant } from "@/interfaces/common/IToastMessage";
import { showErrorAPI } from "@/services/apiRoutes";
import { Org } from "@/services/apiRoutes/routes";
import { CustomTableColumn } from "@/components/common/customTable.vue";
import CustomDropDownItem from "@/components/common/customDropDownItem.vue";
import { IModalMessage } from "@/interfaces/common/common";

const tableFields: CustomTableColumn[] = [
  {
    text: getComponentUiText("app-component-custom-table-header-name-1"),
    value: "domain",
    headerClass:
      "tw-text-[#878787] tw-text-[10px] tw-not-italic tw-font-semibold tw-leading-[100%] tw-tracking-[1px] tw-uppercase",
    sortable: false,
  },
  {
    text: getComponentUiText("app-component-custom-table-header-status-1"),
    value: "isVerified",
    headerClass:
      "tw-text-[#878787] tw-text-[10px] tw-not-italic tw-font-semibold tw-leading-[100%] tw-tracking-[1px] tw-uppercase",
    render: (value: string) =>
      `<span class="tw-text-[#212529] tw-text-[13px] tw-not-italic tw-font-medium tw-leading-[normal]">${value}</span>`,
    sortable: false,
  },
  {
    text: getComponentUiText("app-settings-domains-table-header-date-added-1"),
    value: "fromDate",
    headerClass:
      "tw-text-[#878787] tw-text-[10px] tw-not-italic tw-font-semibold tw-leading-[100%] tw-tracking-[1px] tw-uppercase",
    render: (value: string) =>
      `<span class="tw-text-[#231F20] tw-text-[13px] tw-not-italic tw-font-medium tw-leading-[normal]">${value}</span>`,
    sortable: false,
  },
  {
    text: "",
    value: "action",
    headerClass: "",
    valueClass: "tw-w-[45px] !tw-pr-[16px]",
  },
];

const commonService = new commonServices();
const search: Ref<string> = ref("");
const multipleSelectedDomains: Ref<IDomain[]> = ref([] as IDomain[]);
const singleSelectedDomains: Ref<IDomain> = ref({} as IDomain);
const isShowModalAddDomain: Ref<boolean> = ref(false);
const isShowModalVerifyDomain: Ref<boolean> = ref(false);
const isShowModalManageDomain: Ref<boolean> = ref(false);
const isShowModalVerifyEmail: Ref<boolean> = ref(false);
const showBulkActions: Ref<boolean> = ref(false);
const orgDomains: Ref<IDomain[]> = ref([] as IDomain[]);
const orgDomainsFiltered: Ref<IDomain[]> = ref([] as IDomain[]);
const loadingDomainsFields: Ref<boolean> = ref(true);
const orgDomainsVerify: Ref<IDomain[]> = ref([] as IDomain[]);
const isHandleMultipleSelect: Ref<boolean> = ref(false);
const itemSelectedRow: Ref<any> = ref([]);
let isDeleteModalVisible: Ref<boolean> = ref(false);
const isLoading = computed(() => store.state.Org.isLoadingOrg);
let newDomainValue: Ref<IDomain> = ref({} as IDomain);

const defaultUiText = computed(() => store.state.LanguagePicker.defaultUiText);
const languageSelectedText = computed(
  () => store.state.LanguagePicker.languageSelectedText,
);
const currentOrg = computed(() => store.state.Org.currentOrg);
const backgroundRef = ref(null);

onMounted(async () => {
  getListDomain();
  setMenuItems();
  getCurrentUser();
});

onUnmounted(() => {
  window.removeEventListener("click", () => {});
});

const getListDomain = async () => {
  try {
    const orgId = currentOrg.value?.id;
    if (!orgId) {
      return;
    }
    const res = await getOrgDomains(orgId);
    const domains = get(res, "data.data.domains", []);
    orgDomains.value = domains.map((item) => {
      return { ...item, id: item.domainId };
    });
  } catch (error: any) {
    if (error && error.data) {
      showErrorAPI(error, Org.getDomains);
    }
    orgDomains.value = [];
  }
};

const currentUser: any = ref(null);
const getCurrentUser = async () => {
  if (localStorage.getItem(localStorageKey.currentUserData)) {
    currentUser.value = JSON.parse(
      localStorage.getItem(localStorageKey.currentUserData) || "{}",
    );
  }
};

watch(() => currentOrg.value, getListDomain);
const getCompletedSteps = () => {
  if (orgDomainsVerify.value.length > 0) {
    return orgDomainsVerify.value.filter((item) => item.isVerified === true)
      .length;
  }
  return 0;
};

const getProgressCompelete = () => {
  const total = orgDomainsVerify.value.length;
  const complete = getCompletedSteps();
  return `${Math.round((complete / total) * 100)}%`;
};

const setMenuItems = () => {
  window.addEventListener("click", (e: any) => {
    if (!document.getElementById("clickbox-domain")?.contains(e.target)) {
      (backgroundRef as any).animation &&
        (backgroundRef as any).animation.reloadToggle();
    }
  });
};

const getUiText = (id: string, isArray = false) => {
  return utilities.getUiText(
    id,
    languageSelectedText.value,
    defaultUiText.value,
    isArray,
  );
};

const addDomain = () => {
  newDomainValue.value = {} as IDomain;
  isShowModalAddDomain.value = true;
};

const addDomainSuccess = (domain: IDomain) => {
  // domain response from api is not contain isVerified field.
  if (domain.isVerified === undefined) domain.isVerified = false;
  orgDomains.value = [...orgDomains.value, domain];
  newDomainValue.value = domain;
  showModalVerifyDomain();
};

const showModalVerifyDomain = () => {
  const emitData: IModalMessage = {
    component: MODAL_NAME.VERIFY_ADD_DOMAIN,
    properties: {
      domain: newDomainValue.value,
    },
    events: {
      "update:domain": updateDomainVerified,
      "delete:domain": ($event) => deleteDomainByID($event),
    },
  };

  EventEmitter.emit(EmitterEvents.OPEN_MODAL, emitData);
};

const getSelectedSingleDomain = (id) => {
  if (!id) return;
  const selectedDomain = orgDomains.value.find((item) => item.domainId === id);
  if (selectedDomain) singleSelectedDomains.value = selectedDomain;
};

const getSelectedMultipleDomains = (ids: string[]) => {
  multipleSelectedDomains.value = [];
  if (!ids || ids.length === 0) multipleSelectedDomains.value = [];
  ids.forEach((id) => {
    const selectedDomain = orgDomains.value.find(
      (item) => item.domainId === id,
    );
    if (selectedDomain) multipleSelectedDomains.value.push(selectedDomain);
  });
};

const handleSelectDomain = (ids: string[]) => {
  if (ids.length < 1) {
    isShowMoreActions.value = false;
  }
  itemSelectedRow.value = ids;
  getSelectedMultipleDomains(ids);
  if (multipleSelectedDomains.value.length >= 1) {
    showBulkActions.value = true;
  } else {
    showBulkActions.value = false;
  }
};

watch(
  () => currentOrg.value,
  (newValue, oldValue) => {
    if (newValue.id && oldValue.id && newValue.id !== oldValue.id) {
      loadingDomainsFields.value = true;
      handleSelectDomain([]);
    }
  },
  { deep: true },
);

const handleVerifyDomain = async (domain: IDomain) => {
  try {
    const res = await postOrgDomainVerify(parseInt(domain.domainId) || 0);
    const isVerified = get(res, "data.data.isVerified", false);

    const emitData: IToastMessage = {
      variant: isVerified ? ToastVariant.SUCCESS : ToastVariant.ERROR,
      message: getComponentUiText(
        "app-settings-domain-verify-success-message-2",
        false,
        { domain: domain.domain },
      ),
    };

    if (!isVerified)
      emitData.message = getComponentUiText(
        "app-settings-domain-verify-error-message-1",
        false,
        { domain: domain.domain },
      );

    if (isVerified)
      updateDomainVerified({
        ...domain,
        isVerified: isVerified,
      });

    EventEmitter.emit(EmitterEvents.APP_LAYOUT_SHOW_TOAST_MESSAGE, emitData);
  } catch (error) {
    showErrorAPI(error, Org.orgDomainVerifyCheck);
  }
};

const handleShowModalManageDomain = (id) => {
  getSelectedSingleDomain(id);
  isShowModalManageDomain.value = true;
};

// eslint-disable-next-line no-unused-vars
const deleteDomainByID = (id: string) => {
  getSelectedSingleDomain(id);
  isDeleteModalVisible.value = true;
};

const showModalSendVerificationEmail = async (domain: IDomain) => {
  isShowModalVerifyEmail.value = true;
  singleSelectedDomains.value = domain;
};

const closeModalSendVerificationEmail = () => {
  isShowModalVerifyEmail.value = false;
  singleSelectedDomains.value = {} as IDomain;
};

const deleteDomains = () => {
  if (
    !multipleSelectedDomains.value ||
    multipleSelectedDomains.value.length === 0
  )
    return;
  const emitData: IModalMessage = {
    component: MODAL_NAME.MODAL_RECOMMEN_ARE_YOU_SURE,
    properties: {
      value: {},
      title: getUiText(
        "app-settings-domain-bulk-actions-modal-delete-domains-title-1",
      ),
      description: getUiText(
        "app-settings-domain-bulk-actions-modal-delete-domains-description-1",
      ),
      show: true,
      list: multipleSelectedDomains.value.map((item) => item.domain),
      buttonRed: true,
      buttonText: getUiText("app-common-action-remove-title-1"),
      type: ARE_YOU_SURE_TYPES.settingDomainMultiDelete,
    },
    events: {
      "update:data": deleteMultiDomain,
    },
  };
  EventEmitter.emit(EmitterEvents.OPEN_MODAL, emitData);
  // }
  isHandleMultipleSelect.value = true;
};

const handleUnVerifyDomain = (item) => {
  const emitData: IModalMessage = {
    component: MODAL_NAME.MODAL_RECOMMEN_ARE_YOU_SURE,
    properties: {
      value: {},
      title: getComponentUiText(
        "app-settings-domain-bulk-actions-modal-un-verify-domains-title-1",
        false,
        {
          domainName: item.domain,
        },
      ),
      description: getUiText(
        "app-settings-domain-bulk-actions-modal-un-verify-domains-description-1",
      ),
      show: true,
      buttonRed: true,
      hiddenHelp: false,
      buttonText: getUiText("app-common-action-un-verify-title-1"),
    },
    events: {
      "update:data": async () => {
        loadingDomainsFields.value = true;
        try {
          const res = await unVerifyDomain(item.domainId);
          getListDomain();

          const emitData: IToastMessage = {
            variant: ToastVariant.SUCCESS,
            message: getUiText(
              "app-settings-domain-bulk-actions-modal-un-verify-domains-success-1",
            ),
          };
          EventEmitter.emit(
            EmitterEvents.APP_LAYOUT_SHOW_TOAST_MESSAGE,
            emitData,
          );
        } catch (error) {
          if (error) {
            showErrorAPI(error, Org.unVerifyDomain);
          }
        } finally {
          loadingDomainsFields.value = false;
        }
      },
    },
  };
  EventEmitter.emit(EmitterEvents.OPEN_MODAL, emitData);
};

const handleUnVerifyEmail = (item) => {
  const emitData: IModalMessage = {
    component: MODAL_NAME.MODAL_RECOMMEN_ARE_YOU_SURE,
    properties: {
      value: {},
      title: getComponentUiText(
        "app-settings-domain-bulk-actions-modal-un-verify-domains-title-1",
        false,
        {
          domainName: item.domain,
        },
      ),
      description: getUiText(
        "app-settings-domain-bulk-actions-modal-un-verify-domains-description-1",
      ),
      show: true,
      buttonRed: true,
      hiddenHelp: false,
      buttonText: getUiText("app-common-action-un-verify-title-1"),
    },
    events: {
      "update:data": async () => {
        loadingDomainsFields.value = true;
        try {
          const res = await unVerifyDomainEmail([Number(item.domainId)]);
          getListDomain();

          const emitData: IToastMessage = {
            variant: ToastVariant.SUCCESS,
            message: getUiText(
              "app-settings-domain-bulk-actions-modal-un-verify-emails-success-1",
            ),
          };
          EventEmitter.emit(
            EmitterEvents.APP_LAYOUT_SHOW_TOAST_MESSAGE,
            emitData,
          );
        } catch (error) {
          if (error) {
            showErrorAPI(error, Org.unVerifyDomain);
          }
        } finally {
          loadingDomainsFields.value = false;
        }
      },
    },
  };
  EventEmitter.emit(EmitterEvents.OPEN_MODAL, emitData);
};

const deleteMultiDomain = async () => {
  try {
    store.dispatch("LoadingAreYouSureButtonModal/setLoadingRedButton", true);
    await deleteOrgDomains(itemSelectedRow.value);
    getListDomain();
  } catch (error) {
    if (error) {
      const emitData: IToastMessage = {
        variant: ToastVariant.ERROR,
        message: error.data.error.message,
      };
      EventEmitter.emit(EmitterEvents.APP_LAYOUT_SHOW_TOAST_MESSAGE, emitData);
    }
  } finally {
    store.dispatch("LoadingAreYouSureButtonModal/setLoadingRedButton", false);
  }
};

const isLoadingDelete = ref(false);
const closeDeleteModal = async () => {
  try {
    isLoadingDelete.value = true;
    commonService.setCursorLoading();
    if (isHandleMultipleSelect.value) {
      const ids = multipleSelectedDomains.value.map((d) =>
        parseInt(d.domainId),
      );
      await deleteOrgDomains(ids);
    } else {
      await postDeleteOrgDomain(singleSelectedDomains.value.domainId);
    }
  } catch (error: any) {
    if (error && error.data) {
      showErrorAPI(error, Org.getDomain);
    }
  } finally {
    isHandleMultipleSelect.value = false;
    isDeleteModalVisible.value = false;
    isShowModalManageDomain.value = false;
    isShowModalAddDomain.value = false;
    isShowModalVerifyDomain.value = false;
    isLoadingDelete.value = false;
    commonService.setCursorDefault();
    getListDomain();
  }
};

const filteredData = () => {
  orgDomainsFiltered.value = orgDomains.value
    .filter((post) => {
      return post.domain.toLowerCase().includes(search.value.toLowerCase());
    })
    .map((item) => {
      return {
        ...item,
        isVerified: {
          isVerifiedDNS: item.isVerified,
          isVerifiedEmail: item.isVerifiedEmail ? true : false,
        },
      };
    });
  loadingDomainsFields.value = false;
};

const onPageCompleted = () => {
  if (!loadingDomainsFields.value && isLoading.value)
    EventEmitter.emit(EmitterEvents.PAGE_LOADING_COMPLETED);
};
watch(loadingDomainsFields, onPageCompleted);

const updateDomainVerified = (domain: IDomain) => {
  orgDomains.value = orgDomains.value.map((item) => {
    if (item.domainId === domain.domainId) return domain;
    return item;
  });
};

const watchOrgDomains = () => {
  filteredData();
  orgDomainsVerify.value = orgDomains.value;
};

const onManageDomainModalClose = (action) => {
  if (action === manageDomainActions.delete) {
    isDeleteModalVisible.value = true;
  } else {
    isShowModalManageDomain.value = false;
  }
};

watch(search, filteredData);
watch(orgDomains, watchOrgDomains);
const isShowMoreActions = ref(false);
const handleShowBulkAction = () => {
  isShowMoreActions.value = !isShowMoreActions.value;
  if (multipleSelectedDomains.value.length < 1) {
    isShowMoreActions.value = false;
  }
};
</script>

<style lang="scss" scoped>
.domain-content {
  width: fit-content;
  max-width: 80vw;

  .tw-left-sidebar {
    min-width: 100%;
  }

  .tw-right-sidebar {
    min-width: 241px;
  }

  @media (max-width: 1280px) {
    max-width: 100%;
    width: 100%;
    padding-left: 10px !important;
  }
}

.content {
  flex-direction: row-reverse;
}

.main {
  background: var(--UI-rule-50, rgba(239, 240, 241, 0.5));
  width: 100%;
  height: 100%;
  position: relative;
  overflow-x: hidden;
  overflow-y: auto;

  // .main-content {
  //   padding: 55px 60px 100px;

  //   @media (max-width: 1199px) {
  //     padding: 94px 32px 60px;

  //     @media (max-width: 991px) {
  //       padding: 94px 20px 60px;
  //     }
  //   }

  //   @media (max-width: 767px) {
  //     padding: 94px 10px 60px;
  //   }
  // }
}

@media (max-width: 991px) {
  .icon-input-clear {
    right: 75px !important;
  }
  .header-container {
    height: 60px !important;
  }
}

.header-container {
  line-height: 10px;
  border-bottom: 1px solid #eff0f1;
  height: 132px;
  .header-text {
    color: var(--brand-navy, #211f58);
    font-size: 22px;
    font-family: Poppins;
    font-style: normal;
    font-weight: 600;
    line-height: 22px;
    display: inline-block;
  }
}

.search-box {
  border: none;
  border-radius: 5px;
  padding: 11.5px 15px;
  color: white;
  font-size: 14px;
  font-weight: 500;
  max-width: 450px;
  width: 100%;
  outline: none;
  background: white;
  flex: 1;
  margin-right: 15px;

  ::placeholder {
    color: #b7b7b7;
  }
}

.search-box input {
  border: none;
  outline: none;
  width: 100%;
  margin-left: 10px;
}

@media (max-width: 480px) {
  .search-box {
    // display: none;
    margin: 0;
  }
  .first-row {
    flex-direction: column;
  }
  .bulk__action {
    width: 100%;
    justify-content: start;
    margin-top: 10px;
  }
}

.default-button {
  border: none;
  height: 44px;
  color: #211f58;
  background-color: #ffffff;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  line-height: 100%;
}

.default-button.bulk-actions svg {
  fill: #387dff;
}

.default-button.disable-button {
  color: #b7b7b7;
}

.default-button.disable-button svg {
  fill: #b7b7b7;
}

.tw-right-sidebar {
  border-radius: 6px;
  background: var(--ui-white, #fff);
  padding: 20px;
  width: 241px;
  display: flex;
  align-items: center;

  .radial-progress-container {
    margin-right: 20px;
  }

  .number-per {
    color: var(--brand-navy, #211f58);
    font-size: 28px;
    font-style: normal;
    font-weight: 600;
    line-height: 100%;
    margin-bottom: 5px;
  }

  .text-per {
    color: var(--brand-navy, #211f58);
    font-size: 13px;
    font-family: Poppins;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
  }

  &.skeleton-domain-sidebar {
    height: 118px;
    gap: 20px;
    .skeleton-radial {
      width: 72px;
      height: 72px;
      border-radius: 50%;
    }

    .skeleton-percent {
      width: 76px;
      height: 28px;
      margin-bottom: 12px;
    }

    .skeleton-text {
      width: 68px;
      height: 13px;
    }
  }
}

.tw-left-sidebar {
  flex: 1;
  margin-right: 60px;

  @media (max-width: 1279px) {
    margin-right: 0;
    margin-top: 30px;
    flex: auto;
    width: 100%;
  }

  .first-row {
    margin-bottom: 25px;
  }
}
</style>
